import { AfterViewInit, Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { AccountMapResult, SearchAccountsMapParameters } from '@api-proxies';
import { AccountService } from '@http/clients/account.service';
import { TabNames } from '@models/enums/TabNames';
import { AwaiterService } from '@services/awaiter.service';
import { FilterStateService } from '@services/filter-state.service';
import { MapService, PanelFieldConfig } from '@services/map.service';
import { ICardDetails, MapCardComponent } from '@shared/map-card/map-card.component';
import { CounterBadgeComponent, IconSanitizerService } from 'ngx-lib';
import { Subscription } from 'rxjs';
import { IFilterValueAccounts } from '../accounts-filters/accounts-filter.config';

@Component({
    selector: 'app-accounts-map',
    templateUrl: './accounts-map.component.html',
    styleUrls: ['./accounts-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CounterBadgeComponent, MapCardComponent]
})
export class AccountsMapComponent implements AfterViewInit, OnDestroy, OnInit {
    filters?: IFilterValueAccounts;
    accounts: AccountMapResult[] = [];
    searchConfig: SearchAccountsMapParameters = {} as SearchAccountsMapParameters;
    selectedMarkerData: ICardDetails[] = [];
    accountsCounter?: number;
    closeIcon?: SafeHtml;
    panelConfig: PanelFieldConfig = {
        mainInfo: [
            { label: 'Type', valueKey: 'accountTypeName' },
            { label: 'Markets', valueKey: 'markets' }
        ],
        tooltip: [{ label: 'Product Types', valueKey: 'productTypes' }],
        routerLink: {
            base: '/accounts-single',
            extraKeys: [TabNames[TabNames.Overview]]
        },
        salespersonsKey: 'salesPersons'
    };

    private readonly subscriptions = new Subscription();
    private readonly awaiter = inject(AwaiterService);
    private readonly mapService = inject(MapService);
    private readonly iconSanitizer = inject(IconSanitizerService);
    private readonly filterStateService = inject(FilterStateService<IFilterValueAccounts>);
    private readonly accountService = inject(AccountService);

    ngOnInit(): void {
        this.closeIcon = this.iconSanitizer.getIcon('close');
        this.mapService.setPanelConfig(this.panelConfig);
    }

    async ngAfterViewInit(): Promise<void> {
        await this.mapService.initializeMap('accounts');

        const markerSub = this.mapService.panelData$.subscribe(({ items, count }) => {
            this.selectedMarkerData = items;
            this.accountsCounter = count;
        });
        this.subscriptions.add(markerSub);

        const refreshSub = this.mapService.refresh$.subscribe(() => this.refreshMapView());
        this.subscriptions.add(refreshSub);

        const filterSub = this.filterStateService.stateFilters$.subscribe(async (newFilters) => {
            this.filters = { ...newFilters };
            this.closePanel();
            this.accounts = [];
            this.refreshMapView();
        });
        this.subscriptions.add(filterSub);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    closePanel(): void {
        this.selectedMarkerData = [];
    }

    private async refreshMapView(): Promise<void> {
        this.buildSearchConfig();
        await this.awaiter.awaitAction('Loading Accounts', async () => {
            this.accounts = await this.accountService.searchMap(this.searchConfig);
            this.mapService.fetchData(this.accounts, 'accounts-data');
        });
    }

    private buildSearchConfig(): void {
        const bounds = this.mapService.getMapBounds();
        if (!bounds) return;

        this.searchConfig = {
            latitudeFrom: bounds.getSouth(),
            latitudeTo: bounds.getNorth(),
            longitudeFrom: bounds.getWest(),
            longitudeTo: bounds.getEast(),
            filterText: this.filters?.searchText ?? undefined,
            accountTypeId: this.filters?.accountType?.id ?? undefined,
            marketId: this.filters?.market?.id ?? undefined,
            organizationId: undefined,
            includeArchived: this.filters?.includeArchived ?? undefined,
            salespersons: this.filters?.salespersons?.length
                ? this.filters?.salespersons
                      .map((s) => s.externalId)
                      .filter((externalId): externalId is number => externalId !== undefined)
                : undefined
        } as SearchAccountsMapParameters;
    }
}
